import React, { useEffect, useContext, useState } from "react";
import { SketchfabContext } from "../../Sketchfab/SketchfabContext";
import { ConfigurationContext } from "./ConfigurationContext";
import modelNodes from "./modelNodes";
import modelMaterials from "./modelMaterials";

const RenderIframe = ({ viewerRef }) => {
  const { api, ready, mat } = useContext(SketchfabContext);
  const {
    size,
    heaters: { heater, upgrades },
    interiorexterior: { wood, linerColor },
    accessories: { flueDrain, wooden, electrical, lidCover },
  } = useContext(ConfigurationContext);

  const [sizeNodes, setSizeNodes] = useState(modelNodes["Deluxe"].nodes);
  const [heaterNodes, setHeaterNodes] = useState([]);
  const [upgradeNodes, setUpgradeNodes] = useState([]);
  const [lidCoverNodes, setLidCoverNodes] = useState([]);
  const [flueDrainNodes, setFlueDrainNodes] = useState([]);
  const [woodenNodes, setWoodenNodes] = useState([]);
  const [electricalNodes, setElectricalNodes] = useState([]);

  useEffect(() => {
    if (ready && size) {
      sizeNodes.forEach((node) => api.hide(node));

      const nodes = modelNodes[size?.label ?? "Deluxe"].nodes;

      nodes.forEach((node) => api.show(node));

      setSizeNodes(nodes);
    }
  }, [ready, size]);

  useEffect(() => {
    if (ready && size) {
      const externalIntegrated = "External Integrated"
      const isConnections =
        upgrades.filter((item) => item.label === "90-Degree Heater Connections")
          .length > 0;

      const showNodes =
        heater?.label === "Internal"
          ? modelNodes[size.label].heater[heater.label].show
          : heater?.label === "External"
          ? modelNodes[size.label].heater[heater.label][heater.variant].show
          : heater?.label === externalIntegrated
          ? modelNodes[size.label].heater[heater.label].show
          : heater?.label === "Pahlen Electric"
          ? modelNodes[size.label].heater[heater.label].show
          : [];
      const nodes =
        heater?.label === "Internal"
          ? modelNodes[size.label].heater[heater.label].nodes
          : heater?.label === "External"
          ? isConnections
            ? modelNodes[size.label].heater[heater.label][heater.variant]
                .upgrades["90-Degree Heater Connections"].nodes
            : modelNodes[size.label].heater[heater.label][heater.variant].nodes
          : heater?.label === externalIntegrated
          ? modelNodes[size.label].heater[heater.label].nodes
          : [];

      const oldNodes = [...heaterNodes];
      const newNodes = [...nodes, ...showNodes];

      // External integrated node hiding and showing.
      if (heater?.label === externalIntegrated) {
        modelNodes[size.label].heater[externalIntegrated].hide.forEach((n) => api.hide(n))
      } else {
        modelNodes[size.label].heater[externalIntegrated].hide.forEach((n) => api.show(n))
      }

      oldNodes.forEach((node) => api.hide(node));
      newNodes.forEach((node) => api.show(node));

      setHeaterNodes(nodes);
    }
  }, [ready, heater, upgrades, size]);

  useEffect(() => {
    if (ready && size) {
      const isConnections =
        upgrades.filter((item) => item.label === "90-Degree Heater Connections")
          .length > 0;

      const start =
        heater?.label === "External"
          ? isConnections
            ? modelNodes[size.label].heater["External"][heater.variant]
                .upgrades["90-Degree Heater Connections"].upgrades[
                "External Heater Glass Door"
              ].initial
            : modelNodes[size.label].heater["External"][heater.variant]
                .upgrades["External Heater Glass Door"].initial
          : [];

      let nodes = upgrades.reduce((acc, upgrade) => {
        if (isConnections && upgrade.label === "External Heater Glass Door") {
          const filtered = acc.filter(
            (x) =>
              modelNodes[size.label].heater["External"][
                heater.variant
              ].upgrades["90-Degree Heater Connections"].upgrades[
                "External Heater Glass Door"
              ].initial.indexOf(x) < 0
          );
          return [
            ...filtered,
            ...modelNodes[size.label].heater["External"][heater.variant]
              .upgrades["90-Degree Heater Connections"].upgrades[
              "External Heater Glass Door"
            ].nodes,
          ];
        }

        if (isConnections && upgrade.label === "External Heater Glass Door") {
          const filtered = acc.filter(
            (x) =>
              modelNodes[size.label].heater["External"][
                heater.variant
              ].upgrades["External Heater Glass Door"].initial.indexOf(x) < 0
          );
          return [
            ...filtered,
            ...modelNodes[size.label].heater["External"][heater.variant]
              .upgrades["External Heater Glass Door"].nodes,
          ];
        }
        if (upgrade.label === "Cover Around Internal Heater") {
          return [
            ...acc,
            ...modelNodes[size.label].heater["Internal"].upgrades[
              "Cover Around Internal Heater"
            ].nodes,
          ];
        }
        return acc;
      }, start);

      upgradeNodes.forEach((node) => api.hide(node));

      // Steel strap logic
      const isSteelStrapSelected = wooden.some(w => w.label === "Decorative Stainless Steel Straps")
      const isExternalHeater = heater?.label === "External Integrated"
      const steelStrapNodes = modelNodes[size.label]
          .accessories.wooden["Stainless Steel Straps"][isExternalHeater ? 'simpleRings' : 'fullRings']

      if (isSteelStrapSelected) {
        nodes = [...nodes, ...steelStrapNodes]
      }
      nodes.forEach((node) => api.show(node));
      setUpgradeNodes(nodes);
    }
  }, [ready, heater, upgrades, size]);

  useEffect(() => {
    if (ready && lidCover) {
      lidCoverNodes.forEach((node) => api.hide(node));
      const nodes = lidCover?.label && modelNodes[size.label].accessories.lidCover
        ? modelNodes[size.label].accessories.lidCover[lidCover?.label]
            .nodes
        : modelNodes[size.label].accessories.seating["No Cover"].nodes;

      nodes.forEach((node) => api.show(node));
      setLidCoverNodes(nodes);
    } else {
      lidCoverNodes.forEach((node) => api.hide(node));
    }
  }, [ready, lidCover, heater, size]);

  useEffect(() => {
    if (ready) {
      const isConnections =
        upgrades.filter((item) => item.label === "90-Degree Heater Connections")
          .length > 0;

      flueDrainNodes.forEach((node) => api.hide(node));

      const nodes = flueDrain.reduce((acc, item) => {
        if (item.label === "High-Flow Drain Tap") {
          return [
            ...acc,
            ...modelNodes[size.label].accessories.flueDrain[
              "High-Flow Drain Tap"
            ].nodes,
          ];
        }
        if (
          item.label === "Chimney Heat Guard" &&
          heater?.label === "Internal"
        ) {
          return [
            ...acc,
            ...modelNodes[size.label].heater["Internal"].flueDrain[
              "Chimney Heat Guard"
            ].nodes,
          ];
        }
        if (
          item.label === "Chimney Heat Guard" &&
          heater?.label === "External"
        ) {
          const heatGuardNodes = isConnections
            ? modelNodes[size.label].heater["External"][heater.variant]
                .upgrades["90-Degree Heater Connections"].flueDrain[
                "Chimney Heat Guard"
              ].nodes
            : modelNodes[size.label].heater["External"][heater.variant]
                .flueDrain["Chimney Heat Guard"].nodes;
          return [...acc, ...heatGuardNodes];
        }
        if (
          item.label === "Chimney Heat Guard" &&
          heater?.label === "External Integrated"
        ) {
          return [
            ...acc,
            ...modelNodes[size.label].heater["External Integrated"].flueDrain[
              "Chimney Heat Guard"
            ].nodes,
          ];
        }
        return acc;
      }, []);

      nodes.forEach((node) => api.show(node));
      setFlueDrainNodes(nodes);
    }
  }, [ready, flueDrain, heater, upgrades, size]);

  useEffect(() => {
    if (ready && size) {
      const isStepsSelected =
        wooden.filter((item) => item.label === "Wide Bespoke Steps").length > 0;
      const initialStepNodes =
        modelNodes[size.label].accessories.wooden["Wide Bespoke Steps"].initial;

      // Steel strap logic
      const isSteelStrapSelected = wooden.some(w => w.label === "Decorative Stainless Steel Straps")
      const isExternalHeater = heater?.label === "External Integrated"
      const steelStrapNodes = modelNodes[size.label]
          .accessories.wooden["Stainless Steel Straps"][isExternalHeater ? 'simpleRings' : 'fullRings']

      const start = !isStepsSelected ? initialStepNodes : [];

      [...initialStepNodes, ...woodenNodes, ...steelStrapNodes].forEach((node) => api.hide(node));

      let nodes = wooden.reduce((acc, item) => {
        if (modelNodes[size.label].accessories.wooden[item.label]) {
          return [
            ...acc,
            ...modelNodes[size.label].accessories.wooden[item.label].nodes,
          ];
        }

        return acc;
      }, start);

      // Steel strap logic

      if (isSteelStrapSelected) {
        nodes = [...nodes, ...steelStrapNodes]
      }
      nodes.forEach((node) => api.show(node));
      setWoodenNodes(nodes);
    }
  }, [ready, wooden, size]);

  useEffect(() => {
    if (ready) {
      electricalNodes.forEach((node) => api.hide(node));

      const nodes = electrical.reduce((acc, item) => {
        if (
          item.label === "LED Underwater Lighting" ||
          item.label === "Hydro Massage System"
        ) {
          return [
            ...acc,
            ...modelNodes[size.label].accessories.electrical[item.label][
              item.variant
            ].nodes,
          ];
        }

        if (modelNodes[size.label].accessories.electrical[item.label]) {
          return [
            ...acc,
            ...modelNodes[size.label].accessories.electrical[item.label].nodes,
          ];
        }

        return acc;
      }, []);

      nodes.forEach((node) => api.show(node));
      setElectricalNodes(nodes);
    }
  }, [ready, electrical, size]);

  useEffect(() => {
    if (wood && ready && mat) {
      mat.updateTexture("Wood-SPRUCE", modelMaterials.wood[wood.label]);
    }
  }, [wood, ready, mat]);

  useEffect(() => {
    if (linerColor && ready && mat) {
      mat.updateTexture(
        "WhitePlastic",
        modelMaterials.linerColor[linerColor.label]
      );
    }
  }, [linerColor, ready, mat]);

  return (
    <iframe
      ref={viewerRef}
      title="sketchfab-viewer"
      style={{ height: "100%", width: "100%" }}
    />
  );
};

export default RenderIframe;
